import React, { useState, useRef, useEffect } from "react";
import { Box, useTheme } from "@mui/material";

interface VideoCropOverlayProps {
  videoWidth: number;
  videoHeight: number;
  cropRect: CropRect;
  setCropRect: (crop: CropRect) => void;
  disabled?: boolean;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const HANDLE_SIZE = 10;

const VideoCropOverlay: React.FC<VideoCropOverlayProps> = ({
  videoWidth,
  videoHeight,
  cropRect,
  setCropRect,
  disabled = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<
    "topLeft" | "topRight" | "bottomLeft" | "bottomRight" | "move" | null
  >(null);

  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialRect, setInitialRect] = useState<CropRect>(cropRect);
  const [videoDisplayDimensions, setVideoDisplayDimensions] = useState({
    width: 0,
    height: 0,
    offsetLeft: 0,
    offsetTop: 0,
  });

  useEffect(() => {
    if (containerRef.current && videoWidth > 0 && videoHeight > 0) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;

      const videoAspectRatio = videoWidth / videoHeight;
      let displayWidth = containerWidth;
      let displayHeight = displayWidth / videoAspectRatio;

      if (displayHeight > 400) {
        displayHeight = 400;
        displayWidth = displayHeight * videoAspectRatio;
      }

      const offsetLeft = (containerWidth - displayWidth) / 2;
      const offsetTop = (containerHeight - displayHeight) / 2;

      setVideoDisplayDimensions({
        width: displayWidth,
        height: displayHeight,
        offsetLeft: offsetLeft,
        offsetTop: offsetTop,
      });
    }
  }, [
    videoWidth,
    videoHeight,
    containerRef.current?.offsetWidth,
    containerRef.current?.offsetHeight,
  ]);

  useEffect(() => {
    if (
      (cropRect.width <= 0 || cropRect.height <= 0) &&
      videoWidth > 0 &&
      videoHeight > 0
    ) {
      const width = videoWidth * 0.8;
      const height = videoHeight * 0.8;
      const x = (videoWidth - width) / 2;
      const y = (videoHeight - height) / 2;

      setCropRect({ x, y, width, height });
      console.log("Initialized crop rect:", { x, y, width, height });
    }
  }, [videoWidth, videoHeight, cropRect, setCropRect]);

  const startDrag = (
    e: React.MouseEvent,
    type: "topLeft" | "topRight" | "bottomLeft" | "bottomRight" | "move",
  ) => {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    setDragging(type);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialRect({ ...cropRect });

    document.body.classList.add("video-crop-dragging");
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !containerRef.current || disabled) return;
    if (
      videoDisplayDimensions.width === 0 ||
      videoDisplayDimensions.height === 0
    )
      return;

    const rect = containerRef.current.getBoundingClientRect();

    const mouseX = e.clientX - rect.left - videoDisplayDimensions.offsetLeft;
    const mouseY = e.clientY - rect.top - videoDisplayDimensions.offsetTop;

    const scaleX = videoWidth / videoDisplayDimensions.width;
    const scaleY = videoHeight / videoDisplayDimensions.height;

    const videoMouseX = mouseX * scaleX;
    const videoMouseY = mouseY * scaleY;

    const startX =
      (dragStart.x - rect.left - videoDisplayDimensions.offsetLeft) * scaleX;
    const startY =
      (dragStart.y - rect.top - videoDisplayDimensions.offsetTop) * scaleY;
    const deltaX = videoMouseX - startX;
    const deltaY = videoMouseY - startY;

    let newRect = { ...initialRect };

    if (dragging === "topLeft") {
      const newX = Math.min(
        videoMouseX,
        initialRect.x + initialRect.width - HANDLE_SIZE,
      );
      const newY = Math.min(
        videoMouseY,
        initialRect.y + initialRect.height - HANDLE_SIZE,
      );

      const widthChange = initialRect.x - newX;
      const heightChange = initialRect.y - newY;

      newRect = {
        x: newX,
        y: newY,
        width: Math.max(initialRect.width + widthChange, HANDLE_SIZE),
        height: Math.max(initialRect.height + heightChange, HANDLE_SIZE),
      };
    } else if (dragging === "topRight") {
      const newY = Math.min(
        videoMouseY,
        initialRect.y + initialRect.height - HANDLE_SIZE,
      );
      const heightChange = initialRect.y - newY;

      newRect = {
        x: initialRect.x,
        y: newY,
        width: Math.max(videoMouseX - initialRect.x, HANDLE_SIZE),
        height: Math.max(initialRect.height + heightChange, HANDLE_SIZE),
      };
    } else if (dragging === "bottomLeft") {
      const newX = Math.min(
        videoMouseX,
        initialRect.x + initialRect.width - HANDLE_SIZE,
      );
      const widthChange = initialRect.x - newX;

      newRect = {
        x: newX,
        y: initialRect.y,
        width: Math.max(initialRect.width + widthChange, HANDLE_SIZE),
        height: Math.max(videoMouseY - initialRect.y, HANDLE_SIZE),
      };
    } else if (dragging === "bottomRight") {
      newRect = {
        x: initialRect.x,
        y: initialRect.y,
        width: Math.max(videoMouseX - initialRect.x, HANDLE_SIZE),
        height: Math.max(videoMouseY - initialRect.y, HANDLE_SIZE),
      };
    } else if (dragging === "move") {
      newRect = {
        x: initialRect.x + deltaX,
        y: initialRect.y + deltaY,
        width: initialRect.width,
        height: initialRect.height,
      };
    }

    newRect.x = Math.max(0, Math.min(newRect.x, videoWidth - newRect.width));
    newRect.y = Math.max(0, Math.min(newRect.y, videoHeight - newRect.height));
    newRect.width = Math.min(newRect.width, videoWidth - newRect.x);
    newRect.height = Math.min(newRect.height, videoHeight - newRect.y);

    setCropRect(newRect);
  };

  const stopDrag = () => {
    setDragging(null);
    document.body.classList.remove("video-crop-dragging");
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", stopDrag);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", stopDrag);
      };
    }
  }, [dragging, dragStart, initialRect, videoDisplayDimensions]);

  if (disabled) return null;

  const displayCropRect = {
    x:
      (cropRect.x / videoWidth) * videoDisplayDimensions.width +
      videoDisplayDimensions.offsetLeft,
    y:
      (cropRect.y / videoHeight) * videoDisplayDimensions.height +
      videoDisplayDimensions.offsetTop,
    width: (cropRect.width / videoWidth) * videoDisplayDimensions.width,
    height: (cropRect.height / videoHeight) * videoDisplayDimensions.height,
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: dragging
          ? dragging === "move"
            ? "move"
            : "crosshair"
          : "default",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: `${displayCropRect.y}px`,
          left: `${displayCropRect.x}px`,
          width: `${displayCropRect.width}px`,
          height: `${displayCropRect.height}px`,
          border: `2px solid ${theme.palette.primary.main}`,
          boxSizing: "border-box",
          cursor: "move",
          backgroundColor: "transparent",
          "&:hover": {
            borderWidth: "3px",
          },
        }}
        onMouseDown={(e) => startDrag(e, "move")}
      >
        {[
          {
            position: "topLeft",
            style: {
              top: -HANDLE_SIZE / 2,
              left: -HANDLE_SIZE / 2,
              cursor: "nwse-resize",
            },
          },
          {
            position: "topRight",
            style: {
              top: -HANDLE_SIZE / 2,
              right: -HANDLE_SIZE / 2,
              cursor: "nesw-resize",
            },
          },
          {
            position: "bottomLeft",
            style: {
              bottom: -HANDLE_SIZE / 2,
              left: -HANDLE_SIZE / 2,
              cursor: "nesw-resize",
            },
          },
          {
            position: "bottomRight",
            style: {
              bottom: -HANDLE_SIZE / 2,
              right: -HANDLE_SIZE / 2,
              cursor: "nwse-resize",
            },
          },
        ].map((handle) => (
          <Box
            key={handle.position}
            sx={{
              position: "absolute",
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              backgroundColor: theme.palette.primary.main,
              border: `1px solid ${theme.palette.common.white}`,
              borderRadius: "50%",
              ...handle.style,
            }}
            onMouseDown={(e) => startDrag(e, handle.position as any)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default VideoCropOverlay;
